import { HttpClient } from "./types";
import {
    CryptoHelper,
    configurationEncoder,
    type ConfigurationEncode,
} from "@ecommerce/shared-utils";

export const createAuthService = (httpClient: HttpClient) => {
    const isCookieMode = httpClient.authMode === "http-only-cookie";
    const getEndpoint = (url: string = ""): string => `/auth${url}`;
    const getAuthenticationEndpoint = (url: "/login" | "/register"): string =>
        isCookieMode ? `/auth/session${url}` : getEndpoint(url);

    const postAuthentication = async <TAuthResponse extends { accessToken: string }>(
        endpoint: string,
        payload: unknown,
        eventType: "LOGIN" | "REGISTER",
        config?: any,
    ): Promise<TAuthResponse> => {
        if (isCookieMode) {
            if (!httpClient.postSession || !httpClient.acceptSession) {
                throw new Error("El cliente HTTP no implementa sesiones de tienda por cookie.");
            }

            const response = await httpClient.postSession<TAuthResponse>(endpoint, payload);
            await httpClient.acceptSession(response.accessToken, eventType);
            return response;
        }

        return httpClient.post<TAuthResponse>(endpoint, payload, {
            ...config,
            headers: {
                ...config?.headers,
                Authorization: undefined,
            },
        });
    };

    return {
        authenticateUser: async <TAuthResponse = any>(
            mail: string,
            password: string,
            config?: any,
        ): Promise<TAuthResponse> => {
            const encoder: ConfigurationEncode = await configurationEncoder.getConfiguration();
            const passwordEncrypted = CryptoHelper.encrypt(encoder, password);

            return postAuthentication(
                getAuthenticationEndpoint("/login"),
                {
                    Mail: mail,
                    Password: passwordEncrypted,
                    IpClient: "",
                },
                "LOGIN",
                config,
            ) as Promise<TAuthResponse>;
        },

        registerUser: async <TAuthResponse = any>(
            nombre: string,
            apellido: string,
            codigoArea: string,
            telefono: string,
            mail: string,
            password: string,
            fechaNacimiento: string,
            config?: any,
        ): Promise<TAuthResponse> => {
            const encoder: ConfigurationEncode = await configurationEncoder.getConfiguration();
            const passwordEncrypted = CryptoHelper.encrypt(encoder, password);

            return postAuthentication(
                getAuthenticationEndpoint("/register"),
                {
                    Nombre: nombre,
                    Apellido: apellido,
                    CodigoArea: codigoArea,
                    Telefono: telefono,
                    Mail: mail,
                    Password: passwordEncrypted,
                    IpClient: "",
                    FechaNacimiento: fechaNacimiento,
                },
                "REGISTER",
                config,
            ) as Promise<TAuthResponse>;
        },

        enviarCodigoRecuperoContrasenia: <TRecuperoContraseniaResponse = any>(
            mail: string,
            config?: any,
        ): Promise<TRecuperoContraseniaResponse> => {
            return httpClient.post<TRecuperoContraseniaResponse>(
                getEndpoint("/recupero-contrasenia/enviar-codigo"),
                {
                    Mail: mail,
                },
                {
                    ...config,
                    headers: {
                        ...config?.headers,
                        Authorization: undefined,
                    },
                },
            );
        },

        verificarCodigoRecuperoContrasenia: <TRecuperoContraseniaResponse = any>(
            mail: string,
            codigo: string,
            config?: any,
        ): Promise<TRecuperoContraseniaResponse> => {
            return httpClient.post<TRecuperoContraseniaResponse>(
                getEndpoint("/recupero-contrasenia/verificar-codigo"),
                {
                    Mail: mail,
                    Codigo: codigo,
                },
                {
                    ...config,
                    headers: {
                        ...config?.headers,
                        Authorization: undefined,
                    },
                },
            );
        },

        restablecerContrasenia: async <TRecuperoContraseniaResponse = any>(
            mail: string,
            codigo: string,
            passwordNueva: string,
            config?: any,
        ): Promise<TRecuperoContraseniaResponse> => {
            const encoder: ConfigurationEncode = await configurationEncoder.getConfiguration();
            const passwordEncrypted = CryptoHelper.encrypt(encoder, passwordNueva);

            return httpClient.post<TRecuperoContraseniaResponse>(
                getEndpoint("/recupero-contrasenia/restablecer"),
                {
                    Mail: mail,
                    Codigo: codigo,
                    PasswordNueva: passwordEncrypted,
                },
                {
                    ...config,
                    headers: {
                        ...config?.headers,
                        Authorization: undefined,
                    },
                },
            );
        },

        getUser: <TUser = any>(): Promise<TUser> => {
            return httpClient.get<TUser>(getEndpoint(""));
        },

        updateProfile: <TUser = any>(data: {
            nombre?: string;
            apellido?: string;
            codigoArea?: string;
            telefono?: string;
            fechaNacimiento?: string | null;
        }): Promise<TUser> => {
            return httpClient.put<TUser>(getEndpoint(""), {
                nombre: data.nombre ?? null,
                apellido: data.apellido ?? null,
                codigoArea: data.codigoArea ?? null,
                telefono: data.telefono ?? null,
                fechaNacimiento: data.fechaNacimiento ?? null,
            });
        },

        refreshTokens: <TTokenResponse = any>(): Promise<TTokenResponse> => {
            if (isCookieMode) {
                if (!httpClient.refreshSession) {
                    return Promise.reject(
                        new Error("El cliente HTTP no implementa refresh de sesión."),
                    );
                }
                return httpClient.refreshSession() as Promise<TTokenResponse>;
            }
            return httpClient.post<TTokenResponse>(getEndpoint("/refresh-tokens"), {});
        },

        logout: async (): Promise<void> => {
            if (isCookieMode && httpClient.logoutSession) {
                await httpClient.logoutSession();
                return;
            }

            httpClient.setAccessToken?.(null);
        },
    };
};
